import { Check, Minus, MoreHorizontal, Plus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Money } from '@/components/ds'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Item, Sale } from '@/interfaces/cash-register/Item'
import { cn } from '@/lib/utils'

import { formatQty, itemLabel, lineTotal } from './registerUtils'

const SALE_PERCENTAGES = [0, 10, 15, 20, 30] as const

export type ReceiptLinesProps = {
  items: Item[]
  onRemoveClick: (index: number) => void
  onQuantityChange?: (index: number, newQuantity: number) => void
  onSaleChange?: (index: number, sale: Sale) => void
  onDescriptionChange?: (index: number, description: string | undefined) => void
  isVatPayer?: boolean
  /** Po zaplacení jen pro čtení */
  readOnly?: boolean
}

export type ReceiptLineProps = {
  item: Item
  onRemoveClick: () => void
  onQuantityChange?: (newQuantity: number) => void
  onSaleChange?: (sale: Sale) => void
  onDescriptionChange?: (description: string | undefined) => void
  isVatPayer?: boolean
  readOnly?: boolean
}

const stepButton =
  'inline-flex size-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background hover:text-foreground active:bg-neutral-bg focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none disabled:opacity-40'

export function ReceiptLine({
  item,
  onRemoveClick,
  onQuantityChange,
  onSaleChange,
  onDescriptionChange,
  isVatPayer,
  readOnly,
}: ReceiptLineProps) {
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [descriptionInput, setDescriptionInput] = useState(item.description ?? '')
  const descriptionInputRef = useRef<HTMLInputElement>(null)

  const canRename = !readOnly && !item.productId && !!onDescriptionChange
  const hasSale = item.sale != null && item.sale.value > 0
  const label = itemLabel(item)

  useEffect(() => {
    if (isEditingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus()
      descriptionInputRef.current.select()
    }
  }, [isEditingDescription])

  const startRename = () => {
    setDescriptionInput(item.description ?? '')
    setIsEditingDescription(true)
  }

  const handleDescriptionSave = () => {
    const newDescription = descriptionInput.trim() || undefined
    if (newDescription !== item.description) onDescriptionChange?.(newDescription)
    setIsEditingDescription(false)
  }

  const handleDescriptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleDescriptionSave()
    } else if (e.key === 'Escape') {
      setDescriptionInput(item.description ?? '')
      setIsEditingDescription(false)
    }
  }

  const decrement = () => {
    if (item.amount <= 1) {
      onRemoveClick()
      return
    }
    onQuantityChange?.(Math.max(0.01, Number((item.amount - 1).toFixed(3))))
  }

  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-0.5 rounded-lg bg-background py-2 pr-1.5 pl-3 @[27rem]:grid-cols-[minmax(0,1fr)_auto_6rem_auto]">
      <div className="col-start-1 row-start-1 min-w-0 py-1">
        {isEditingDescription ? (
          <input
            ref={descriptionInputRef}
            type="text"
            value={descriptionInput}
            onChange={(e) => setDescriptionInput(e.target.value)}
            onBlur={handleDescriptionSave}
            onKeyDown={handleDescriptionKeyDown}
            placeholder="Název položky"
            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
        ) : canRename ? (
          <button
            type="button"
            onClick={startRename}
            className={cn('line-clamp-2 text-left text-sm font-medium hover:underline', !label && 'text-muted-foreground')}
            title="Přejmenovat položku"
          >
            {label || 'Vlastní položka'}
          </button>
        ) : (
          <p className="line-clamp-2 text-sm font-medium">{label || 'Vlastní položka'}</p>
        )}
        <p className="mt-0.5 text-xs text-muted-foreground tabular">
          {formatQty(item.amount)} ×{' '}
          <Money amount={item.unitPrice} minor={false} size="sm" className="font-normal text-muted-foreground" />
          {isVatPayer && item.taxRate !== undefined && `, DPH ${item.taxRate} %`}
          {hasSale && item.sale?.type === 'percent' && <span className="text-foreground">, sleva {item.sale.value} %</span>}
        </p>
      </div>

      {!readOnly && onQuantityChange && (
        <div className="col-start-1 row-start-2 -ml-3 flex items-center justify-self-start @[27rem]:col-start-2 @[27rem]:row-start-1 @[27rem]:ml-0">
          <button type="button" className={stepButton} onClick={decrement} aria-label={item.amount <= 1 ? 'Odebrat položku' : 'Ubrat kus'}>
            {item.amount <= 1 ? <Trash2 className="size-4" /> : <Minus className="size-4" />}
          </button>
          <span className="min-w-7 text-center text-sm font-semibold tabular">{formatQty(item.amount)}</span>
          <button type="button" className={stepButton} onClick={() => onQuantityChange(item.amount + 1)} aria-label="Přidat kus">
            <Plus className="size-4" />
          </button>
        </div>
      )}

      <div className="col-start-2 row-start-1 flex flex-col items-end self-start pt-1 @[27rem]:col-start-3">
        <Money amount={lineTotal(item)} minor={false} animate />
        {hasSale && (
          <Money amount={item.amount * item.unitPrice} minor={false} size="sm" className="font-normal text-muted-foreground line-through" />
        )}
      </div>

      {!readOnly && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={cn(stepButton, 'col-start-2 row-start-2 justify-self-end @[27rem]:col-start-4 @[27rem]:row-start-1')} aria-label="Další akce s položkou">
              <MoreHorizontal className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {onSaleChange && (
              <>
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Sleva na položku</DropdownMenuLabel>
                {SALE_PERCENTAGES.map((pct) => {
                  const current = (item.sale?.type === 'percent' ? item.sale.value : 0) === pct
                  return (
                    <DropdownMenuItem key={pct} className="h-10" onSelect={() => onSaleChange({ type: 'percent', value: pct })}>
                      <span className="flex-1 tabular">{pct === 0 ? 'Bez slevy' : `${pct} %`}</span>
                      {current && <Check className="size-4" />}
                    </DropdownMenuItem>
                  )
                })}
                <DropdownMenuSeparator />
              </>
            )}
            {canRename && (
              <DropdownMenuItem className="h-10" onSelect={startRename}>
                Přejmenovat
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="h-10 text-danger focus:text-danger" onSelect={onRemoveClick}>
              <Trash2 className="text-danger" />
              Odebrat z účtenky
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  )
}

export function ReceiptLines({
  items,
  onRemoveClick,
  onQuantityChange,
  onSaleChange,
  onDescriptionChange,
  isVatPayer,
  readOnly,
}: ReceiptLinesProps) {
  const endRef = useRef<HTMLDivElement>(null)
  const count = items.length

  // nová položka se má ukázat, i když je účtenka dlouhá
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
  }, [count])

  return (
    <>
      <ul className="flex flex-col gap-2 py-1">
        {items.map((item, idx) => (
          <ReceiptLine
            key={idx}
            item={item}
            readOnly={readOnly}
            onRemoveClick={() => onRemoveClick(idx)}
            onQuantityChange={onQuantityChange ? (q) => onQuantityChange(idx, q) : undefined}
            onSaleChange={onSaleChange ? (sale) => onSaleChange(idx, sale) : undefined}
            onDescriptionChange={onDescriptionChange ? (d) => onDescriptionChange(idx, d) : undefined}
            isVatPayer={isVatPayer}
          />
        ))}
      </ul>
      <div ref={endRef} />
    </>
  )
}
